import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { PatientPortalService } from '../../_services/patient-portal/patient-portal.service';

interface AvailableSlot {
  calendars_id: number;
  start_utc: string;
  end_utc: string;
  start_local: string;
  end_local: string;
  timezone?: string;
  professionals_count?: number;
}

interface AvailableProfessional {
  users_id: number;
  calendars_id: number;
  name: string;
  surname: string;
  picture?: string | null;
  calendar?: { id: number; name: string };
}

interface CalendarCell {
  key: string;
  label: string;
  currentMonth: boolean;
  available: boolean;
  selected: boolean;
}

@Component({
  selector: 'app-public-booking-manage',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './public-booking-manage.component.html',
  styleUrl: './public-booking-manage.component.scss',
})
export class PublicBookingManageComponent implements OnInit {
  customerIdentifier = '';
  siteIdentifier = '';
  token = '';

  loading = true;
  actionLoading = false;
  errorMessage = '';

  bootstrap: any = null;
  booking: any = null;
  currentMonthKey = '';
  monthAvailabilityDays = new Map<string, number>();
  selectedDayKey = '';
  availableSlots: AvailableSlot[] = [];
  availableProfessionals: AvailableProfessional[] = [];
  selectedSlot: AvailableSlot | null = null;
  selectedProfessionalUsersId: number | null = null;
  selectedAppointmentId: number | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly portalService: PatientPortalService
  ) {}

  ngOnInit(): void {
    this.customerIdentifier = `${this.route.snapshot.paramMap.get('customerIdentifier') || ''}`;
    this.siteIdentifier = `${this.route.snapshot.paramMap.get('siteIdentifier') || ''}`;
    this.token = `${this.route.snapshot.paramMap.get('token') || ''}`;
    this.currentMonthKey = this.visibleMonthKeys()[0] || this.formatMonthKey(new Date());
    this.loadInitialData();
  }

  get monthLabel(): string {
    const [year, month] = this.currentMonthKey.split('-').map(Number);
    if (!year || !month) return '';
    return new Intl.DateTimeFormat('es-ES', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(year, month - 1, 1));
  }

  get canGoToPreviousMonth(): boolean {
    const months = this.visibleMonthKeys();
    return months.indexOf(this.currentMonthKey) > 0;
  }

  get canGoToNextMonth(): boolean {
    const months = this.visibleMonthKeys();
    const index = months.indexOf(this.currentMonthKey);
    return index >= 0 && index < months.length - 1;
  }

  get selectedAppointment(): any | null {
    return (this.booking?.appointments || []).find(
      (appointment: any) => Number(appointment?.id || 0) === Number(this.selectedAppointmentId || 0)
    ) || null;
  }

  get usesProfessionalRouting(): boolean {
    return this.bootstrap?.bookingSettings?.routing_mode === 'BY_PROFESSIONAL';
  }

  get calendarCells(): CalendarCell[] {
    const [year, month] = this.currentMonthKey.split('-').map(Number);
    if (!year || !month) return [];

    const firstOfMonth = new Date(year, month - 1, 1);
    const startWeekday = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells: CalendarCell[] = [];

    for (let index = 0; index < startWeekday; index += 1) {
      cells.push({
        key: `blank-${index}`,
        label: '',
        currentMonth: false,
        available: false,
        selected: false,
      });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const key = `${this.currentMonthKey}-${`${day}`.padStart(2, '0')}`;
      cells.push({
        key,
        label: `${day}`,
        currentMonth: true,
        available: this.monthAvailabilityDays.has(key),
        selected: key === this.selectedDayKey,
      });
    }

    return cells;
  }

  private loadInitialData(): void {
    this.loading = true;
    this.errorMessage = '';

    this.portalService
      .bootstrap(this.customerIdentifier, this.siteIdentifier)
      .subscribe({
        next: (response: any) => {
          this.bootstrap = response;
        },
      });

    this.portalService
      .getManagedBooking(this.customerIdentifier, this.siteIdentifier, this.token)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          this.booking = response;
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.router.navigate(['/404']);
            return;
          }
          this.errorMessage = 'No hemos podido cargar esta reserva o el enlace ya no es válido.';
        },
      });
  }

  cancelAppointment(appointment: any): void {
    if (!appointment?.can_cancel || this.actionLoading) return;
    const confirmed = window.confirm('¿Quieres cancelar esta cita?');
    if (!confirmed) return;

    this.actionLoading = true;
    this.portalService
      .cancelManagedBookingAppointment(
        this.customerIdentifier,
        this.siteIdentifier,
        this.token,
        appointment.id
      )
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: (response: any) => {
          this.booking = response;
          this.errorMessage = '';
          if (Number(this.selectedAppointmentId || 0) === Number(appointment.id)) {
            this.closeReschedule();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = `${error?.error?.message || 'No hemos podido cancelar la cita.'}`;
        },
      });
  }

  startReschedule(appointment: any): void {
    if (!appointment?.can_reschedule) return;
    this.selectedAppointmentId = Number(appointment.id);
    this.currentMonthKey = this.extractDayKey(appointment.start).slice(0, 7);
    this.selectedDayKey = '';
    this.selectedSlot = null;
    this.availableSlots = [];
    this.availableProfessionals = [];
    this.selectedProfessionalUsersId = null;
    this.loadRescheduleMonth();
  }

  closeReschedule(): void {
    this.selectedAppointmentId = null;
    this.selectedDayKey = '';
    this.selectedSlot = null;
    this.availableSlots = [];
    this.availableProfessionals = [];
    this.selectedProfessionalUsersId = null;
    this.monthAvailabilityDays.clear();
  }

  previousMonth(): void {
    const months = this.visibleMonthKeys();
    const index = months.indexOf(this.currentMonthKey);
    if (index > 0) {
      this.currentMonthKey = months[index - 1];
      this.selectedDayKey = '';
      this.selectedSlot = null;
      this.selectedProfessionalUsersId = null;
      this.loadRescheduleMonth();
    }
  }

  nextMonth(): void {
    const months = this.visibleMonthKeys();
    const index = months.indexOf(this.currentMonthKey);
    if (index >= 0 && index < months.length - 1) {
      this.currentMonthKey = months[index + 1];
      this.selectedDayKey = '';
      this.selectedSlot = null;
      this.selectedProfessionalUsersId = null;
      this.loadRescheduleMonth();
    }
  }

  private loadRescheduleMonth(): void {
    if (!this.selectedAppointmentId) return;

    this.actionLoading = true;
    this.monthAvailabilityDays.clear();

    this.portalService
      .getManagedBookingRescheduleAvailability(
        this.customerIdentifier,
        this.siteIdentifier,
        this.token,
        {
          appointments_id: this.selectedAppointmentId,
          month: this.currentMonthKey,
        }
      )
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: (response: any) => {
          for (const row of response?.days || []) {
            const key = `${row?.day || ''}`.trim();
            const count = Number(row?.slots_count || 0);
            if (key && count > 0) {
              this.monthAvailabilityDays.set(key, count);
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = `${error?.error?.message || 'No hemos podido consultar la disponibilidad.'}`;
        },
      });
  }

  selectCalendarDay(dayKey: string): void {
    if (!this.selectedAppointmentId || !this.monthAvailabilityDays.has(dayKey)) return;

    this.actionLoading = true;
    this.selectedDayKey = dayKey;
    this.selectedSlot = null;
    this.availableProfessionals = [];
    this.selectedProfessionalUsersId = null;

    this.portalService
      .getManagedBookingRescheduleAvailability(
        this.customerIdentifier,
        this.siteIdentifier,
        this.token,
        {
          appointments_id: this.selectedAppointmentId,
          day: dayKey,
        }
      )
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: (response: any) => {
          this.availableSlots = Array.isArray(response?.slots) ? response.slots : [];
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = `${error?.error?.message || 'No hemos podido consultar las horas disponibles.'}`;
        },
      });
  }

  selectSlot(slot: AvailableSlot): void {
    this.selectedSlot = slot;
    this.availableProfessionals = [];
    this.selectedProfessionalUsersId = null;

    if (!this.usesProfessionalRouting || !this.selectedAppointmentId || !this.selectedDayKey) {
      return;
    }

    this.actionLoading = true;
    this.portalService
      .getManagedBookingRescheduleAvailability(
        this.customerIdentifier,
        this.siteIdentifier,
        this.token,
        {
          appointments_id: this.selectedAppointmentId,
          day: this.selectedDayKey,
          start_utc: slot.start_utc,
          end_utc: slot.end_utc,
        }
      )
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: (response: any) => {
          this.availableProfessionals = Array.isArray(response?.professionals)
            ? response.professionals
            : [];
        },
      });
  }

  selectProfessional(usersId: number | null): void {
    this.selectedProfessionalUsersId = usersId;
  }

  confirmReschedule(): void {
    if (!this.selectedAppointmentId || !this.selectedSlot) {
      this.errorMessage = 'Selecciona una nueva hora para continuar.';
      return;
    }

    const professional =
      this.availableProfessionals.find(
        (item) => Number(item.users_id) === Number(this.selectedProfessionalUsersId)
      ) || null;

    this.actionLoading = true;
    this.portalService
      .rescheduleManagedBookingAppointment(
        this.customerIdentifier,
        this.siteIdentifier,
        this.token,
        {
          appointments_id: this.selectedAppointmentId,
          calendars_id: professional?.calendars_id || this.selectedSlot.calendars_id,
          users_id: professional?.users_id || null,
          start_utc: this.selectedSlot.start_utc,
          end_utc: this.selectedSlot.end_utc,
        }
      )
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: (response: any) => {
          this.booking = response;
          this.errorMessage = '';
          this.closeReschedule();
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = `${error?.error?.message || 'No hemos podido reprogramar la cita.'}`;
        },
      });
  }

  formatTimeRange(slot?: {
    start?: string;
    end?: string;
    start_local?: string;
    end_local?: string;
    start_utc?: string;
    end_utc?: string;
  }): string {
    const start = this.parseDate(
      slot?.start_local || slot?.start_utc || slot?.start
    );
    const end = this.parseDate(slot?.end_local || slot?.end_utc || slot?.end);
    if (!start || !end) return '';

    const dayLabel = new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    }).format(start);
    const timeFormatter = new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${dayLabel} · ${timeFormatter.format(start)} - ${timeFormatter.format(end)}`;
  }

  getProfessionalName(professional?: AvailableProfessional | null): string {
    return `${professional?.name || ''} ${professional?.surname || ''}`.trim();
  }

  visibleMonthKeys(): string[] {
    const totalDays = Math.min(
      Number(this.bootstrap?.bookingSettings?.show_days_ahead || 14),
      90
    );
    const now = new Date();
    const months = new Set<string>();

    for (let index = 0; index < Math.max(totalDays, 1); index += 1) {
      const date = new Date(now);
      date.setDate(now.getDate() + index);
      months.add(this.formatMonthKey(date));
    }

    return Array.from(months.values());
  }

  private extractDayKey(value?: string): string {
    return `${value || ''}`.slice(0, 10);
  }

  private formatMonthKey(date: Date): string {
    return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}`;
  }

  private parseDate(value?: string): Date | null {
    const raw = `${value || ''}`.trim();
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}
